/**
 * StyledDialog - Componente de diálogo reutilizable con diseño moderno
 *
 * VARIANTES:
 * - default: Diseño estándar con header completo
 * - compact: Diseño compacto con glassmorphism (recomendado para formularios simples)
 *
 * USO COMPACTO (recomendado):
 * import { StyledDialog } from '../common';
 *
 * <StyledDialog
 *   open={dialogOpen}
 *   onClose={handleClose}
 *   title="Título"
 *   icon={<WhatsAppIcon />}
 *   variant="compact"
 *   maxWidth="xs"
 * >
 *   <StyledDialog.Body>
 *     <TextField label="Campo" />
 *   </StyledDialog.Body>
 *   <StyledDialog.CompactActions>
 *     <StyledDialog.CancelButton onClick={handleClose} />
 *     <StyledDialog.PrimaryButton onClick={handleSave}>Guardar</StyledDialog.PrimaryButton>
 *   </StyledDialog.CompactActions>
 * </StyledDialog>
 *
 * USO ESTÁNDAR:
 * <StyledDialog
 *   open={dialogOpen}
 *   onClose={handleClose}
 *   title="Título del Diálogo"
 *   subtitle="Descripción opcional"
 *   icon={<Icon />}
 *   chipLabel="Etiqueta"
 * >
 *   <StyledDialog.InfoBox title="Info">Mensaje</StyledDialog.InfoBox>
 *   <StyledDialog.FormSection>
 *     <StyledDialog.FormField label="Campo" required>
 *       <TextField />
 *     </StyledDialog.FormField>
 *   </StyledDialog.FormSection>
 *   <StyledDialog.Actions>
 *     <StyledDialog.CancelButton onClick={handleClose} />
 *     <StyledDialog.PrimaryButton onClick={handleSave}>Guardar</StyledDialog.PrimaryButton>
 *   </StyledDialog.Actions>
 * </StyledDialog>
 */

import React from 'react';
import {
  Dialog,
  DialogActions,
  Box,
  Typography,
  Chip,
  Button,
  Slide,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';

// Transición estándar (slide desde abajo)
const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Colores del tema
export const DIALOG_THEME = {
  // Header
  headerGradient: 'linear-gradient(135deg, #164e63 0%, #0891b2 100%)',
  headerText: '#fff',
  iconColor: '#fbbf24',
  iconBgColor: 'rgba(255,255,255,0.15)',

  // Info box
  infoBg: '#eff6ff',
  infoBorder: '#bfdbfe',
  infoTitle: '#1e40af',
  infoText: '#3b82f6',

  // Labels y texto
  labelColor: '#164e63',
  textColor: '#374151',
  mutedColor: '#6b7280',

  // Botones
  primaryBg: '#164e63',
  primaryHover: '#0891b2',
  primaryShadow: '0 4px 14px 0 rgba(30, 58, 95, 0.39)',
  primaryShadowHover: '0 6px 20px rgba(30, 58, 95, 0.45)',
  cancelBg: '#f1f5f9',
  cancelHover: '#e2e8f0',

  // Fondos
  dialogBg: 'rgba(255, 255, 255, 0.95)',
  dialogBgSolid: '#f8fafc',
  contentBg: '#fff',
  borderColor: '#e2e8f0',
  borderSubtle: 'rgba(0,0,0,0.06)',

  // Sombras
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  boxShadowLight: '0 8px 32px rgba(99, 102, 241, 0.15)',

  // Glassmorphism
  backdropBlur: 'blur(20px)',

  // Input focus
  inputFocusBorder: '#164e63'
};

// Estilos para inputs con el tema
export const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    fontSize: 13,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: DIALOG_THEME.inputFocusBorder },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: DIALOG_THEME.inputFocusBorder }
  },
  '& .MuiInputLabel-root': { fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: DIALOG_THEME.inputFocusBorder }
};

// Componente principal del diálogo
const StyledDialog = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  chipLabel,
  chipColor,
  maxWidth = 'sm',
  fullWidth = true,
  disableClose = false,
  variant = 'default', // 'default' | 'compact'
  children
}) => {
  const isCompact = variant === 'compact';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={disableClose ? undefined : onClose}
      TransitionComponent={SlideTransition}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : (isCompact ? 4 : 3),
          background: isCompact ? DIALOG_THEME.dialogBg : DIALOG_THEME.dialogBgSolid,
          backdropFilter: isCompact ? DIALOG_THEME.backdropBlur : 'none',
          boxShadow: isCompact ? DIALOG_THEME.boxShadow : DIALOG_THEME.boxShadowLight,
          overflow: 'hidden',
          border: isCompact && !isMobile ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
          m: isMobile ? 0 : undefined,
          maxHeight: isMobile ? '100%' : undefined
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: DIALOG_THEME.headerGradient,
          color: DIALOG_THEME.headerText,
          display: 'flex',
          alignItems: 'center',
          gap: isCompact ? { xs: 1.5, sm: 2 } : { xs: 1, sm: 1.5 },
          py: isCompact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
          px: { xs: 2, sm: 3 }
        }}
      >
        {icon && (
          <Box
            sx={{
              width: isCompact ? 40 : 44,
              height: isCompact ? 40 : 44,
              borderRadius: isCompact ? '12px' : 2,
              bgcolor: DIALOG_THEME.iconBgColor,
              backdropFilter: isCompact ? 'blur(10px)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& .MuiSvgIcon-root': {
                color: DIALOG_THEME.iconColor,
                fontSize: isCompact ? 22 : 26
              }
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant={isCompact ? 'body1' : 'h6'}
            sx={{
              fontWeight: 700,
              fontSize: isCompact ? { xs: 14, sm: 16 } : { xs: 16, sm: 18 },
              wordBreak: 'break-word',
              lineHeight: 1.3
            }}
          >
            {title}
          </Typography>
          {subtitle && !isCompact && (
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {chipLabel && (
          <Chip
            label={chipLabel}
            size="small"
            sx={{
              height: isCompact ? 22 : 'auto',
              background: chipColor || 'rgba(255,255,255,0.2)',
              color: '#fff',
              fontWeight: isCompact ? 700 : 600,
              fontSize: isCompact ? 10 : 11
            }}
          />
        )}
      </Box>

      {/* Contenido */}
      {children}
    </Dialog>
  );
};

// Sub-componente: Body para contenido con padding (responsivo)
StyledDialog.Body = ({ children, compact = false, sx = {} }) => (
  <Box
    sx={{
      p: compact ? { xs: 2, sm: 2.5 } : { xs: 2, sm: 3 },
      display: 'flex',
      flexDirection: 'column',
      gap: compact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
      flex: 1,
      overflowY: 'auto',
      ...sx
    }}
  >
    {children}
  </Box>
);

// Sub-componente: Caja de información
StyledDialog.InfoBox = ({ title, children, sx = {} }) => (
  <Box
    sx={{
      p: 2,
      mx: 3,
      mt: 3,
      bgcolor: DIALOG_THEME.infoBg,
      borderRadius: 2,
      border: `1px solid ${DIALOG_THEME.infoBorder}`,
      ...sx
    }}
  >
    {title && (
      <Typography
        sx={{
          fontSize: 13,
          color: DIALOG_THEME.infoTitle,
          fontWeight: 600,
          mb: 0.5
        }}
      >
        {title}
      </Typography>
    )}
    <Typography sx={{ fontSize: 12, color: DIALOG_THEME.infoText }}>
      {children}
    </Typography>
  </Box>
);

// Sub-componente: Sección de formulario (diseño estándar)
StyledDialog.FormSection = ({ children, sx = {} }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2.5,
      p: 3,
      ...sx
    }}
  >
    {children}
  </Box>
);

// Sub-componente: Campo de formulario con label (diseño estándar)
StyledDialog.FormField = ({ label, required, children }) => (
  <Box>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: DIALOG_THEME.labelColor,
        mb: 1,
        textTransform: 'uppercase'
      }}
    >
      {label} {required && '*'}
    </Typography>
    {children}
  </Box>
);

// Sub-componente: Texto de ayuda
StyledDialog.HelperText = ({ children }) => (
  <Typography sx={{ fontSize: 11, color: DIALOG_THEME.mutedColor, mt: 0.5 }}>
    {children}
  </Typography>
);

// Sub-componente: Acciones estándar
StyledDialog.Actions = ({ children, sx = {} }) => (
  <DialogActions
    sx={{
      borderTop: `1px solid ${DIALOG_THEME.borderColor}`,
      background: DIALOG_THEME.contentBg,
      px: 3,
      py: 2,
      gap: 1.5,
      ...sx
    }}
  >
    {children}
  </DialogActions>
);

// Sub-componente: Acciones compactas (botones full-width lado a lado)
StyledDialog.CompactActions = ({ children, sx = {} }) => (
  <Box
    sx={{
      px: { xs: 2, sm: 2.5 },
      py: { xs: 1.5, sm: 2 },
      display: 'flex',
      gap: { xs: 1, sm: 1.5 },
      borderTop: `1px solid ${DIALOG_THEME.borderSubtle}`,
      flexShrink: 0,
      ...sx
    }}
  >
    {children}
  </Box>
);

// Sub-componente: Botón cancelar
StyledDialog.CancelButton = ({
  onClick,
  disabled,
  fullWidth = false,
  children = 'Cancelar'
}) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    fullWidth={fullWidth}
    sx={{
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      color: '#64748b',
      bgcolor: DIALOG_THEME.cancelBg,
      borderColor: DIALOG_THEME.borderColor,
      px: fullWidth ? 2 : 3,
      py: fullWidth ? 1 : 0.75,
      '&:hover': { bgcolor: DIALOG_THEME.cancelHover }
    }}
  >
    {children}
  </Button>
);

// Sub-componente: Botón primario
StyledDialog.PrimaryButton = ({
  onClick,
  disabled,
  loading,
  startIcon,
  fullWidth = false,
  children
}) => (
  <Button
    onClick={onClick}
    disabled={disabled || loading}
    variant="contained"
    fullWidth={fullWidth}
    startIcon={!loading && startIcon}
    sx={{
      borderRadius: 2,
      textTransform: 'none',
      fontWeight: 600,
      bgcolor: DIALOG_THEME.primaryBg,
      px: fullWidth ? 2 : 3,
      py: fullWidth ? 1 : 0.75,
      boxShadow: fullWidth ? DIALOG_THEME.primaryShadow : 'none',
      '&:hover': {
        bgcolor: DIALOG_THEME.primaryHover,
        boxShadow: fullWidth ? DIALOG_THEME.primaryShadowHover : 'none'
      }
    }}
  >
    {loading ? <CircularProgress size={20} color="inherit" /> : children}
  </Button>
);

// Sub-componente: Lista de items seleccionables
StyledDialog.SelectableList = ({ children, maxHeight = 280, sx = {} }) => (
  <Box
    sx={{
      maxHeight,
      overflow: 'auto',
      bgcolor: DIALOG_THEME.contentBg,
      borderRadius: 2,
      border: `1px solid ${DIALOG_THEME.borderColor}`,
      ...sx
    }}
  >
    {children}
  </Box>
);

// Sub-componente: Item seleccionable
StyledDialog.SelectableItem = ({
  onClick,
  icon,
  title,
  subtitle,
  endIcon,
  isLast = false
}) => (
  <Box
    onClick={onClick}
    sx={{
      p: 2,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      borderBottom: isLast ? 'none' : `1px solid ${DIALOG_THEME.borderColor}`,
      transition: 'all 0.2s',
      '&:hover': {
        bgcolor: 'rgba(30, 58, 95, 0.05)',
        '& .end-icon': { opacity: 1, transform: 'translateX(0)' }
      }
    }}
  >
    {icon && (
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: DIALOG_THEME.infoBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': {
            color: DIALOG_THEME.labelColor,
            fontSize: 22
          }
        }}
      >
        {icon}
      </Box>
    )}
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: '#64748b', fontSize: 11 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {endIcon && (
      <Box
        className="end-icon"
        sx={{
          opacity: 0.5,
          transform: 'translateX(-5px)',
          transition: 'all 0.2s',
          '& .MuiSvgIcon-root': {
            color: DIALOG_THEME.labelColor,
            fontSize: 20
          }
        }}
      >
        {endIcon}
      </Box>
    )}
  </Box>
);

export default StyledDialog;
